// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

// Branches exhaustively on the ebiose-core discriminator `model_type`
// (cf. ebiose-core/ebiose/core/enums.py:AgentModelType). The `default`
// arm calls assertNever — adding a new variant in ebiose-core without a
// TS update produces a compile-time `never`-type error (no `any`, no cast).
import { GraphCard } from "./graphCard"

// If a new AgentModel branch is added to types.d.ts without updating the
// switch, tsc routes here with the new branch typed (not narrowed to `never`)
// → tsc --noEmit errors, breaking loudly rather than silently.
function assertNever(value: never): never {
  throw new Error(
    `Unhandled AgentModel variant — TS exhaustiveness check failed: ${JSON.stringify(value)}`,
  )
}

function BasicEvolverModelView({ model }: { model: BasicEvolverModel }) {
  return (
    <section
      aria-label="Basic evolver agent model"
      className="box space-y-3 p-[18px]"
    >
      <header className="flex flex-row items-center gap-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-text-tertiary">
          model_type
        </span>
        <span className="font-mono text-sm text-foreground">
          {model.model_type}
        </span>
      </header>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3.5 gap-y-1.5 text-sm">
        <dt className="text-text-tertiary">n_selected_agents_from_ecosystem</dt>
        <dd className="text-foreground">
          {model.n_selected_agents_from_ecosystem}
        </dd>
      </dl>
    </section>
  )
}

function GeneticEvolverModelView({
  model,
}: {
  model: GeneticEvolverModel
}) {
  return (
    <section
      aria-label="Genetic evolver agent model"
      className="box space-y-3 p-[18px]"
    >
      <header className="flex flex-row items-center gap-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-text-tertiary">
          model_type
        </span>
        <span className="font-mono text-sm text-foreground">
          {model.model_type}
        </span>
      </header>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3.5 gap-y-1.5 text-sm text-foreground">
        <dt className="text-text-tertiary">mode</dt>
        <dd className="font-mono">{model.mode}</dd>
        <dt className="text-text-tertiary">n_agents_in_population</dt>
        <dd>{model.n_agents_in_population}</dd>
        <dt className="text-text-tertiary">n_selected_agents_from_ecosystem</dt>
        <dd>{model.n_selected_agents_from_ecosystem}</dd>
        <dt className="text-text-tertiary">n_best_agents_to_return</dt>
        <dd>{model.n_best_agents_to_return}</dd>
        <dt className="text-text-tertiary">replacement_ratio</dt>
        <dd>{model.replacement_ratio}</dd>
        <dt className="text-text-tertiary">tournament_size_ratio</dt>
        <dd>{model.tournament_size_ratio}</dd>
        <dt className="text-text-tertiary">n_elite</dt>
        <dd>{model.n_elite}</dd>
        {model.n_generations !== undefined && model.n_generations !== null ? (
          <>
            <dt className="text-text-tertiary">n_generations</dt>
            <dd>{model.n_generations}</dd>
          </>
        ) : null}
        {model.budget !== undefined && model.budget !== null ? (
          <>
            <dt className="text-text-tertiary">budget</dt>
            <dd>{model.budget}</dd>
          </>
        ) : null}
      </dl>
    </section>
  )
}

export function AgentModelRenderer({ model }: { model: AgentModel }) {
  // Exhaustive switch on the discriminator. The `default` arm
  // routes to `assertNever` — adding a fourth branch to AgentModel
  // in `types.d.ts` will surface here as a `tsc --noEmit` error
  // (the value will no longer narrow to `never`).
  switch (model.model_type) {
    case "Graph":
      return <GraphCard model={model} />
    case "basic_evolver":
      return <BasicEvolverModelView model={model} />
    case "genetic_evolver":
      return <GeneticEvolverModelView model={model} />
    default:
      return assertNever(model)
  }
}
